import { useBookmarks } from "@circle-react-shared/Bookmarks/hooks/useBookmarks";
import { Loader } from "@circle-react-uikit/Loader";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useAnyHoverMediaQuery } from "@circle-react/hooks/useMediaQuery";
import type { BookmarkType } from "@circle-react/types/Bookmarks";
import InfiniteScroll from "react-infinite-scroll-component";

import { BookmarkItemWrapper } from "./BookmarkItemWrapper";
import { USER_BOOKMARKS_PANEL_SCROLL_ELEMENT_ID } from "./constants";
import { ActionMenu } from "./Item/ActionMenu";
import { ItemSelector } from "./Item/ItemSelector";
import { ListWrapper } from "./ListWrapper";
import { NoBookmarks } from "./NoBookmarks";

interface BookmarksListProps {
  activeTab?: BookmarkType;
  scrollableTarget?: string;
}

export const BookmarksList = ({
  activeTab = "post",
  scrollableTarget = USER_BOOKMARKS_PANEL_SCROLL_ELEMENT_ID,
}: BookmarksListProps) => {
  const { getBookmarks, hasNextPage, fetchNextPage, isLoading } = useBookmarks({
    bookmarkType: activeTab,
  });
  const canHover = useAnyHoverMediaQuery();

  if (isLoading) {
    return (
      <ListWrapper>
        <Loader center />
      </ListWrapper>
    );
  }

  const bookmarks = getBookmarks();
  if (!bookmarks.length) return <NoBookmarks activeTab={activeTab} />;

  return (
    <ListWrapper>
      <InfiniteScroll
        next={fetchNextPage}
        hasMore={!!hasNextPage}
        loader={<Loader center />}
        dataLength={bookmarks.length}
        style={{ overflow: "initial" }}
        scrollableTarget={scrollableTarget}
      >
        <div className="flex flex-col gap-1">
          {bookmarks.map(bookmark => (
            <div
              className="hover:bg-secondary has-[[data-popup-open]]:bg-secondary has-[:focus-visible]:bg-secondary group flex w-full items-center justify-between rounded-xl px-4 py-3 transition-colors duration-200 ease-in-out"
              key={bookmark.id}
            >
              <BookmarkItemWrapper bookmark={bookmark} bookmarkType={activeTab}>
                <div className="flex items-start">
                  <div
                    className="flex w-full flex-col space-y-1"
                    data-testid="bookmarks-details"
                  >
                    <ItemSelector bookmark={bookmark} />
                  </div>
                </div>
              </BookmarkItemWrapper>
              <div
                className={classNames(
                  "self-start transition-opacity duration-150",
                  {
                    "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 has-[:focus-visible]:pointer-events-auto has-[:focus-visible]:opacity-100 has-[[data-popup-open]]:pointer-events-auto has-[[data-popup-open]]:opacity-100":
                      canHover,
                  },
                )}
              >
                <ActionMenu
                  recordId={bookmark.record_id}
                  bookmarkId={bookmark.id}
                  bookmarkType={activeTab}
                  bookmarkRecord={bookmark.bookmark_record}
                />
              </div>
            </div>
          ))}
        </div>
      </InfiniteScroll>
    </ListWrapper>
  );
};
